import {
  ALERT_COLOR_PRESETS,
  ALERT_ICON_OPTIONS,
  normalizeColor,
  contrastText,
} from '@/widgets/alertShortcuts'

export { ALERT_COLOR_PRESETS as MACRO_COLOR_PRESETS, ALERT_ICON_OPTIONS as MACRO_ICON_OPTIONS, contrastText }

export const MACRO_ADAPTERS = [
  { value: 'obs', label: 'OBS' },
  { value: 'propresenter', label: 'ProPresenter' },
  { value: 'qlc', label: 'QLC+' },
  { value: 'resolume', label: 'Resolume' },
  { value: 'studioone', label: 'Studio One' },
  { value: 'atem', label: 'ATEM' },
  { value: 'system', label: 'System' },
]

export const MACRO_STEP_KINDS = [
  { value: 'command', label: 'Command' },
  { value: 'macro', label: 'Run QLC macro' },
  { value: 'alert', label: 'Alert' },
  { value: 'delay', label: 'Delay' },
]

export const MACRO_CUSTOM_ACTION = '__custom__'

export const MACRO_ACTIONS = {
  obs: [
    { value: 'SetCurrentProgramScene', label: 'Change scene', fetch: 'obs-scene' },
    { value: 'StartStream', label: 'Start streaming' },
    { value: 'StopStream', label: 'Stop streaming' },
    { value: 'StartRecord', label: 'Start recording' },
    { value: 'StopRecord', label: 'Stop recording' },
  ],
  qlc: [
    {
      value: 'setWidgetState',
      label: 'Switch button',
      fetch: 'qlc-widget',
      fields: [{ key: 'on', label: 'State', type: 'boolean', default: true }],
    },
    {
      value: 'setGrandMaster',
      label: 'Set grand master',
      fields: [{ key: 'value', label: 'Value', type: 'number', min: 0, max: 255, default: 255 }],
    },
    {
      value: 'switchPage',
      label: 'Switch page',
      fields: [{ key: 'page', label: 'Page', type: 'number', min: 1, max: 99, default: 1 }],
    },
  ],
  propresenter: [
    { value: 'triggerNext', label: 'Next' },
    { value: 'triggerPrevious', label: 'Previous' },
    { value: 'clear', label: 'Clear' },
    {
      value: 'triggerLook',
      label: 'Trigger look',
      fields: [
        { key: 'name', label: 'Look name', type: 'text', default: '' },
        { key: 'id', label: 'Look ID (optional)', type: 'text', default: '' },
      ],
    },
    {
      value: 'triggerPlaylistItem',
      label: 'Trigger playlist item',
      fields: [
        { key: 'playlistId', label: 'Playlist ID', type: 'text', default: '' },
        { key: 'index', label: 'Item index', type: 'number', min: 0, default: 0 },
      ],
    },
    {
      value: 'setStageLayout',
      label: 'Set stage layout',
      fields: [
        { key: 'screenId', label: 'Screen ID', type: 'text', default: '' },
        { key: 'layoutId', label: 'Layout ID', type: 'text', default: '' },
      ],
    },
    // legacy aliases kept for existing macros
    { value: 'triggerSlide', label: 'Next slide (legacy)' },
    { value: 'slideTrigger', label: 'Previous slide (legacy)', fixedPayload: { index: -1 } },
  ],
  resolume: [
    {
      value: 'triggerColumn',
      label: 'Select column',
      fields: [{ key: 'columnIndex', label: 'Column', type: 'number', min: 1, default: 1 }],
    },
    { value: 'nextColumn', label: 'Next column' },
    { value: 'prevColumn', label: 'Previous column' },
    { value: 'clearAll', label: 'Clear all' },
    {
      value: 'clearLayer',
      label: 'Clear layer',
      fields: [{ key: 'layerIndex', label: 'Layer', type: 'number', min: 1, default: 1 }],
    },
    {
      value: 'triggerClip',
      label: 'Trigger clip',
      fields: [
        { key: 'layerIndex', label: 'Layer', type: 'number', min: 1, default: 1 },
        { key: 'columnIndex', label: 'Clip / column', type: 'number', min: 1, default: 1 },
      ],
    },
  ],
  studioone: [
    { value: 'play', label: 'Play' },
    { value: 'stop', label: 'Stop' },
    { value: 'record', label: 'Record' },
  ],
  atem: [
    { value: 'cut', label: 'Cut' },
    { value: 'auto', label: 'Auto transition' },
    {
      value: 'setPreview',
      label: 'Set preview input',
      fields: [{ key: 'inputId', label: 'Input ID', type: 'number', min: 1, default: 1 }],
    },
    {
      value: 'take',
      label: 'Take (preview → program)',
      fields: [{ key: 'inputId', label: 'Input ID (optional)', type: 'number', min: 1, default: 0 }],
    },
    {
      value: 'setProgram',
      label: 'Set program input',
      fields: [{ key: 'inputId', label: 'Input ID', type: 'number', min: 1, default: 1 }],
    },
  ],
  system: [
    {
      value: 'setReady',
      label: 'Set ready',
      fields: [{ key: 'ready', label: 'Ready', type: 'boolean', default: true }],
    },
  ],
}

export const DEFAULT_MACROS = []

export function macroActionsFor(adapter) {
  return MACRO_ACTIONS[adapter] || []
}

export function findMacroAction(adapter, action) {
  if (!adapter || !action) return null
  // Legacy macros used triggerWidget (= switch on with value 255)
  const key = adapter === 'qlc' && action === 'triggerWidget' ? 'setWidgetState' : action
  return macroActionsFor(adapter).find((a) => a.value === key) || null
}

export function defaultFieldValues(def) {
  const out = {}
  if (!def || !Array.isArray(def.fields)) return out
  def.fields.forEach((f) => {
    if (f.type === 'boolean') out[f.key] = f.default != null ? f.default : false
    else if (f.type === 'number') out[f.key] = f.default != null ? f.default : 0
    else out[f.key] = f.default != null ? f.default : ''
  })
  return out
}

export function actionSelectValue(step) {
  if (!step) return ''
  if (step.customAction) return MACRO_CUSTOM_ACTION
  const def = findMacroAction(step.adapter, step.action)
  if (def) return def.value
  return step.action ? MACRO_CUSTOM_ACTION : ''
}

export function parseCommandResult(res) {
  const msg = res && res.result
  if (!msg) return null
  if (msg.type === 'error' || msg.error) {
    throw new Error(msg.error || 'Command failed')
  }
  let payload = msg.payload
  if (typeof payload === 'string') {
    try {
      payload = JSON.parse(payload)
    } catch (e) {
      return null
    }
  }
  return payload
}

function commandFormExtras(step) {
  let action = step.action
  if (step.adapter === 'qlc' && action === 'triggerWidget') {
    action = 'setWidgetState'
  }
  const def = findMacroAction(step.adapter, action)
  const payload = step.payload || {}
  const extras = {
    payloadText: Object.keys(payload).length ? JSON.stringify(payload, null, 2) : '',
    targetValue: '',
    fieldValues: {},
    customAction: !def,
    action: def ? action : step.action,
  }
  if (!def) return extras
  if (def.fetch === 'obs-scene') {
    extras.targetValue = String(payload.sceneName || '')
  } else if (def.fetch === 'qlc-widget') {
    extras.targetValue = payload.widgetId != null ? String(payload.widgetId) : ''
    const fv = defaultFieldValues(def)
    if (payload.on != null) fv.on = !!payload.on
    else if (payload.value != null) fv.on = Number(payload.value) >= 200
    extras.fieldValues = fv
  } else if (def.fields) {
    const fv = defaultFieldValues(def)
    def.fields.forEach((f) => {
      if (payload[f.key] != null) fv[f.key] = payload[f.key]
    })
    extras.fieldValues = fv
  }
  return extras
}

export function commandPayloadFromForm(step) {
  const def = findMacroAction(step.adapter, step.action)
  if (!def || step.customAction) {
    return normalizePayload(step.payloadText != null ? step.payloadText : step.payload)
  }
  if (def.fixedPayload) return { ...def.fixedPayload }
  if (def.fetch === 'obs-scene') return { sceneName: String(step.targetValue || '') }
  if (def.fetch === 'qlc-widget') {
    const id = Number(step.targetValue)
    const on = !step.fieldValues || step.fieldValues.on !== false
    return {
      widgetId: Number.isFinite(id) ? id : step.targetValue,
      on,
      value: on ? 255 : 0,
    }
  }
  if (def.fields) {
    const out = {}
    def.fields.forEach((f) => {
      const v = step.fieldValues ? step.fieldValues[f.key] : undefined
      if (f.type === 'number') out[f.key] = Number(v)
      else if (f.type === 'boolean') out[f.key] = !!v
      else out[f.key] = v
    })
    return out
  }
  return {}
}

export function blankMacroStep(kind = 'command') {
  if (kind === 'alert') {
    return {
      kind: 'alert',
      nodeId: 'all',
      message: '',
      color: '#ffbc34',
      icon: 'bell',
    }
  }
  if (kind === 'delay') {
    return { kind: 'delay', delayMs: 500 }
  }
  if (kind === 'macro') {
    return { kind: 'macro', macroId: null }
  }
  const adapter = 'obs'
  const action = 'SetCurrentProgramScene'
  return {
    kind: 'command',
    nodeId: '',
    adapter,
    action,
    payload: {},
    payloadText: '',
    targetValue: '',
    fieldValues: {},
    customAction: false,
  }
}

export function blankMacro() {
  return {
    label: '',
    color: '#4798e8',
    icon: 'play',
    stopOnError: true,
    steps: [blankMacroStep('command')],
  }
}

function normalizePayload(raw) {
  if (raw == null || raw === '') return {}
  if (typeof raw === 'object' && !Array.isArray(raw)) return { ...raw }
  if (typeof raw === 'string') {
    const t = raw.trim()
    if (!t) return {}
    try {
      const parsed = JSON.parse(t)
      if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) return parsed
    } catch (_) {
      /* keep empty */
    }
  }
  return {}
}

function normalizeStep(raw) {
  if (!raw || typeof raw !== 'object') return null
  const kind = ['command', 'alert', 'delay', 'macro'].includes(raw.kind) ? raw.kind : 'command'

  if (kind === 'delay') {
    const delayMs = Math.max(0, Math.min(60000, Number(raw.delayMs) || 0))
    return { kind: 'delay', delayMs }
  }

  if (kind === 'alert') {
    const message = String(raw.message || '').trim()
    if (!message) return null
    return {
      kind: 'alert',
      nodeId: String(raw.nodeId || 'all'),
      message,
      color: normalizeColor(raw.color),
      icon: String(raw.icon || 'bell').replace(/^fa-/, '') || 'bell',
    }
  }

  if (kind === 'macro') {
    const macroId = Number(raw.macroId)
    if (!Number.isFinite(macroId) || macroId <= 0) return null
    return { kind: 'macro', macroId }
  }

  const adapter = String(raw.adapter || '').trim()
  const action = String(raw.action || '').trim()
  const nodeId = String(raw.nodeId || '').trim()
  if (!adapter || !action || !nodeId) return null
  return {
    kind: 'command',
    nodeId,
    adapter,
    action,
    payload: normalizePayload(raw.payload),
  }
}

export function normalizeMacros(raw) {
  if (!Array.isArray(raw)) return []
  return raw
    .map((item) => {
      if (!item || typeof item !== 'object') return null
      const label = String(item.label || '').trim()
      if (!label) return null
      const steps = Array.isArray(item.steps)
        ? item.steps.map(normalizeStep).filter(Boolean)
        : []
      return {
        label,
        color: normalizeColor(item.color || '#4798e8'),
        icon: String(item.icon || 'play').replace(/^fa-/, '') || 'play',
        stopOnError: item.stopOnError !== false,
        steps,
      }
    })
    .filter(Boolean)
}

/** Macros from QLC Macros page are tagged with lightbulb icon. */
export const QLC_MACRO_ICON = 'lightbulb'
export const QLC_MACRO_COLOR = '#f0ad4e'

export function isQlcMacro(macro) {
  if (!macro) return false
  return String(macro.icon || '').replace(/^fa-/, '') === QLC_MACRO_ICON
}

/** Expected QLC button statuses after running a sequence (incl. nested). */
export function collectQlcStatusFromMacro(macro, byId, seen = new Set()) {
  const out = {}
  if (!macro || !Array.isArray(macro.steps)) return out
  const id = Number(macro.id)
  if (Number.isFinite(id) && id > 0) {
    if (seen.has(id)) return out
    seen.add(id)
  }
  macro.steps.forEach((s) => {
    if (!s) return
    if (s.kind === 'macro') {
      const nested = typeof byId === 'function' ? byId(s.macroId) : null
      Object.assign(out, collectQlcStatusFromMacro(nested, byId, seen))
      return
    }
    if (s.kind === 'command' && s.adapter === 'qlc' && (s.action === 'triggerWidget' || s.action === 'setWidgetState')) {
      const wid = Number(s.payload?.widgetId)
      if (!Number.isFinite(wid)) return
      let value = 255
      if (s.payload?.on != null) value = s.payload.on ? 255 : 0
      else if (s.payload?.value != null) value = Number(s.payload.value)
      out[wid] = Number.isFinite(value) ? value : 255
    }
  })
  return out
}

export function qlcButtonStep(nodeId, widgetId, name = '', value = 255) {
  const v = value == null || value === '' ? 255 : Number(value)
  const on = Number.isFinite(v) ? v >= 200 : true
  return {
    kind: 'command',
    nodeId: String(nodeId || ''),
    adapter: 'qlc',
    action: 'setWidgetState',
    payload: {
      widgetId: Number(widgetId),
      on,
      value: on ? 255 : 0,
      _label: String(name || `Button ${widgetId}`),
    },
  }
}

export function qlcDelayStep(ms = 300) {
  return {
    kind: 'delay',
    delayMs: Math.max(0, Math.min(60000, Number(ms) || 0)),
  }
}

/** Editor form: known actions use dropdowns; custom keeps JSON textarea */
export function macrosToForm(raw) {
  const list = normalizeMacros(raw == null ? DEFAULT_MACROS : raw)
  return list.map((m) => ({
    ...m,
    steps: m.steps.map((s) => {
      if (s.kind === 'command') {
        return { ...s, ...commandFormExtras(s) }
      }
      return { ...s }
    }),
  }))
}

export function macrosFromForm(raw) {
  if (!Array.isArray(raw)) return []
  return normalizeMacros(
    raw.map((m) => ({
      ...m,
      steps: Array.isArray(m.steps)
        ? m.steps.map((s) => {
            if (s.kind === 'command') {
              return {
                ...s,
                payload: commandPayloadFromForm(s),
              }
            }
            return s
          })
        : [],
    }))
  )
}

export function sleep(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms))
}
